import { useSession } from "@supabase/auth-helpers-react";
import { useCallback, useEffect, useState } from "react";
import { showErrorNotification } from "../helpers/notifications";
import { useRedux } from "./useRedux";
import { SelectedChatDTO } from "../@DTO/selectedChat";
import { useChatStore } from "../state/chat";
import { getBearerToken } from "../helpers/localStorageHelpers";

export interface MessageEndData {
  event: string;
  content: string;
  data: {
    user: string;
    date: string;
    datetime: string;
    chat_request: string;
    role: string;
    parentMongoId: any;
    assistantId: string;
    channelId: string;
    flagImage: boolean;
    userEmail: string;
    _id: string;
  };
  messageId: string;
}

const useChatWebSocket = () => {
  const [message] = useState<any>("");
  const [isConnected, setIsConnected] = useState(false);
  const [webSocket, setWebSocket] = useState<any>(null);
  const [localInterval, setLocalInterval] = useState<any>(null);

  const {
    setStatus,
    setEndMessage,
    setBroadcastMessage,
    updateMessage,
    errorReceiveMessageManager,
    reset,
  } = useChatStore();

  const { useAppSelector } = useRedux();
  const { selectedChat } = useAppSelector(state => ({
    selectedChat: state.Chats.selectedChat as SelectedChatDTO,
  }));
  const session = useSession();
  const [retryCount, setRetryCount] = useState(1);

  const createWebSocket = useCallback(() => {
    const socketDomain = process.env.REACT_APP_WEBSOCKET_CHAT_API_URL;

    const ws = new WebSocket(
      `${socketDomain}?channelId=${123}&authorization=${getBearerToken()}`
    );

    return ws;
  }, [selectedChat]);

  function sendWebsocketMessage(message) {
    if (!retryCount) return;

    if (webSocket && isConnected) {
      return webSocket.send(message);
    }

    const ws = createWebSocket();

    ws.onopen = () => {
      setWebSocket(ws);
      setIsConnected(true);
      ws.send(message);
    };

    ws.onmessage = ({ data }) => {
      if (data === "ping") {
        ws.send("pong");
        return;
      }

      if (!isValidMessage(data)) {
        return;
      }

      updateSocketMessage(data);
    };
  }

  function updateSocketMessage(data: string) {
    try {
        const message = JSON.parse(data);
    
        // Get current conversationId from window global
        const currentConversationId = (window as any).currentConversationId;
        
        // Check if message has a conversationId and if it matches the current one
        if (message.conversationId && currentConversationId && 
            message.conversationId !== currentConversationId) {
            return;
        }
        
        const scrollToBottom = (behavior: ScrollBehavior = 'auto', force: boolean = false) => {
            const conversationElement = document.querySelector('.chat-conversation');
            if (!conversationElement) return;
            
            const isNearBottom = conversationElement.scrollHeight - conversationElement.scrollTop - conversationElement.clientHeight < 100;
            
            if (force || isNearBottom) {
                conversationElement.scrollTo({
                    top: conversationElement.scrollHeight,
                    behavior
                });
            }
        };
        
        switch (message.event) {
            case "token":
                if (message.data.trim() !== "" && message.data.trim() !== undefined ) {
                    setBroadcastMessage(message);
                    // Progressive scroll during generation (only if user is near bottom)
                    scrollToBottom('auto');
                }
                break;

            case "end":
                setEndMessage("[DONE]");
                // Always scroll to bottom when message is complete
                setTimeout(() => scrollToBottom('smooth', true), 100);
                break;

            case "error":
                showErrorNotification(message.message);
                reset();
                break;

            default:
                console.warn("Unknown event", message.event);
        }
    } catch (error) {
        console.error("Error at procesing the websocket message", error);
    }
}

  function isValidMessage(data) {
    const jsonRegex = /^[\],:{}\s]*$/;
    const sanitizedData = data
      .replace(/\\["\\\/bfnrtu]/g, "@")
      .replace(
        /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?/g,
        "]"
      );
    return jsonRegex.test(sanitizedData);
  }

  useEffect(() => {
    if (!session?.access_token || !retryCount) return;

    const ws = createWebSocket();

    ws.onopen = () => {
      setWebSocket(ws);
      setIsConnected(true);
      reset();
    };

    ws.onmessage = ({ data }) => {
      if (data === "ping") {
        ws.send("pong");
        return;
      }

      if (!isValidMessage(data)) {
        return;
      }

      updateSocketMessage(data);
    };

    ws.onerror = error => {
      console.error(`WebSocket error:, ${error}`);
      reset();
    };

    ws.onclose = () => {
      setWebSocket(null);
      setIsConnected(false);
      setRetryCount(retryCount + 1);
    };

    return () => {
      if (webSocket) {
        webSocket.close();
      }
      if (ws) {
        ws.close();
      }
    };
  }, []);

  useEffect(() => {
    function sendPing() {
      if (webSocket.readyState === WebSocket.OPEN) {
        webSocket.send("ping");
      } else {
        setWebSocket(null);
      }
    }
    if (localInterval) {
      clearInterval(localInterval);
    }

    if (webSocket) {
      const newInterval = setInterval(sendPing, 10000);
      setLocalInterval(newInterval);
    }

    return () => {
      if (localInterval) {
        clearInterval(localInterval);
      }
    };
  }, [webSocket]);

  const closeWebSocket = useCallback(() => {
    if (webSocket) {
      webSocket.close();
      setWebSocket(null);
      setIsConnected(false);
    }
  }, [webSocket]);

  return {
    sendWebsocketMessage,
    message,
    closeWebSocket,
  };
};

export default useChatWebSocket;
